import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { 
  Receipt, 
  Eye, 
  ArrowLeft,
  CheckCircle,
  Clock,
  Plus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TransactionDetailsWithBreakdown from '@/components/TransactionDetailsWithBreakdown';

const InvoiceBreakdownDemo: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTransaction, setSelectedTransaction] = useState<string | null>(null);

  // Mock transaction data
  const mockTransactions = [
    {
      id: 'LBGYVH',
      type: 'Invoice token',
      amount: 1.00,
      currency: 'USD',
      status: 'Completed',
      created: 'July 16, 2025 at 01:54 PM GMT+2',
      description: 'Coffee and pastry order',
      tokensCount: 3,
      icon: <Receipt className="w-5 h-5 text-orange-600" />,
      bgColor: 'bg-orange-100'
    },
    {
      id: 'XYZ789',
      type: 'Invoice token',
      amount: 25.50,
      currency: 'USD',
      status: 'Partial',
      created: 'July 16, 2025 at 10:30 AM GMT+2',
      description: 'Lunch delivery order',
      tokensCount: 2,
      icon: <Receipt className="w-5 h-5 text-orange-600" />,
      bgColor: 'bg-orange-100'
    },
    {
      id: 'PQR456',
      type: 'Invoice token',
      amount: 5.75,
      currency: 'USD',
      status: 'Completed',
      created: 'July 15, 2025 at 03:22 PM GMT+2',
      description: 'Grocery store purchase',
      tokensCount: 1,
      icon: <Receipt className="w-5 h-5 text-orange-600" />,
      bgColor: 'bg-orange-100'
    },
    {
      id: 'MNO123',
      type: 'Invoice token',
      amount: 100.00,
      currency: 'USD',
      status: 'Pending',
      created: 'July 15, 2025 at 09:15 AM GMT+2',
      description: 'Monthly subscription payment',
      tokensCount: 5,
      icon: <Receipt className="w-5 h-5 text-orange-600" />,
      bgColor: 'bg-orange-100'
    }
  ];

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return currency + ' ';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Partial': return 'bg-yellow-100 text-yellow-800';
      case 'Pending': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed': return <CheckCircle className="w-3 h-3 mr-1" />;
      case 'Partial': return <Clock className="w-3 h-3 mr-1" />;
      case 'Pending': return <Clock className="w-3 h-3 mr-1" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back Home</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Invoice Payment Breakdown</h1>
              <p className="text-gray-600">View how cash tokens were applied to invoice payments</p>
            </div>
          </div>
        </div>

        {/* Demo Instructions */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Receipt className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">Invoice Token Breakdown Demo</h3>
                <p className="text-sm text-blue-800 mt-1">
                  This demo shows how invoice payments are broken down into the individual cash tokens 
                  that were applied to pay them. Click "View Details" on any transaction to see the breakdown.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transaction List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Receipt className="w-5 h-5" />
              <span>Recent Invoice Transactions</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {mockTransactions.map((transaction) => (
                <div 
                  key={transaction.id}
                  className="flex items-center justify-between p-4 bg-white border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-lg ${transaction.bgColor}`}>
                      {transaction.icon}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-gray-900">{transaction.id}</span>
                        <Badge className={getStatusColor(transaction.status)}>
                          {getStatusIcon(transaction.status)}
                          {transaction.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {transaction.description}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {transaction.created} • {transaction.tokensCount} token{transaction.tokensCount !== 1 ? 's' : ''} applied
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">
                        +{getCurrencySymbol(transaction.currency)}{transaction.amount.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500">{transaction.currency}</div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedTransaction(transaction.id)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Feature Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Token Transparency</h4>
                  <p className="text-sm text-gray-600">See exactly which tokens were used for each payment</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Payment Timeline</h4>
                  <p className="text-sm text-gray-600">Track when each token was applied to the invoice</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Plus className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Multi-Token Support</h4>
                  <p className="text-sm text-gray-600">Handle invoices paid with multiple tokens</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Usage Examples */}
        <Card>
          <CardHeader>
            <CardTitle>Common Use Cases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Split Bill Payments</h4>
                <p className="text-sm text-gray-600">
                  When multiple people contribute tokens to pay a single invoice, 
                  you can see each person's contribution clearly.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Partial Payments</h4>
                <p className="text-sm text-gray-600">
                  Track progress on invoices that are paid over time with multiple 
                  tokens until the full amount is reached.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Audit Trail</h4>
                <p className="text-sm text-gray-600">
                  Maintain a complete record of all tokens used for accounting 
                  and reconciliation purposes.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Refund Tracking</h4>
                <p className="text-sm text-gray-600">
                  If refunds are needed, easily identify which tokens were used 
                  and their original creators.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transaction Details Modal */}
      {selectedTransaction && (
        <TransactionDetailsWithBreakdown
          transactionId={selectedTransaction}
          isOpen={!!selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
    </div>
  );
};

export default InvoiceBreakdownDemo; 